/**
 * Destinazione sicura per il ponte app mobile -> sito.
 *
 * L'app apre /accesso-associati?bridge=<codice>[&next=/percorso]: `next` viene passato
 * SOLO a navigate() del router, mai a window.location, e deve restare un path interno.
 * E' una whitelist, non una lista di divieti, allineata al backend (Task 10):
 * - deve iniziare con un solo `/` non seguito da `/` o `\` (via niente URL assoluti,
 *   niente protocol-relative `//host`, niente `/\host` che alcuni browser normalizzano);
 * - il resto puo' contenere solo caratteri leciti in un path/query interno: tutto il
 *   resto (backslash, spazi, caratteri di controllo, newline) fa cadere nel default;
 * - niente `/area-associativa` (case-insensitive): e' l'area dei rappresentanti, che usa
 *   un token diverso (representative_token);
 * - niente `bridge=` nella query: un `next` che riporta alla pagina di login con un codice
 *   ponte rientrerebbe nell'effetto con un codice ormai consumato;
 * - niente segmenti `.`/`..`: il router normalizza `/area-riservata/../area-associativa`
 *   prima di risolvere la rotta, aggirando il divieto sul prefisso; vale anche con `?`/`#`
 *   subito dopo (`/area-riservata/..?x` diventa `/?x`) e per i punti codificati (`%2e%2e`,
 *   che il parser URL del browser rilegge come `..`). Gemella di backend/utils/bridgeNext.js.
 */
export const BRIDGE_DEFAULT_NEXT = '/area-riservata';

const PATH_INTERNO = /^\/(?![/\\])[A-Za-z0-9_\-./?=&%#:@+~,;]*$/;
const DOT_SEGMENT = /(^|\/)(?:\.|%2e){1,2}(?=$|[/?#])/i;

export const resolveBridgeNext = (next: string | null): string => {
  if (!next) return BRIDGE_DEFAULT_NEXT;
  if (!PATH_INTERNO.test(next)) return BRIDGE_DEFAULT_NEXT;
  if (DOT_SEGMENT.test(next)) return BRIDGE_DEFAULT_NEXT;
  if (next.toLowerCase().startsWith('/area-associativa')) return BRIDGE_DEFAULT_NEXT;
  if (/[?&]bridge=/i.test(next)) return BRIDGE_DEFAULT_NEXT;
  return next;
};

/**
 * Ponte app -> sito del RAPPRESENTANTE (/accesso-associati?bridge=<codice>&tipo=rappresentante
 * [&next=/area-associativa/...]): stesse regole, ma la destinazione deve stare nell'area
 * associativa (il token salvato e' representative_token) e senza separatori codificati
 * (`%2e`, `%2f`, `%5c`), che il browser rilegge come `..`/`/`/`\`.
 * Allineata a validRepresentativeNext di backend/utils/bridgeNext.js.
 */
export const REPRESENTATIVE_BRIDGE_DEFAULT_NEXT = '/area-associativa';

const AREA_ASSOCIATIVA = /^\/area-associativa(?=$|[/?#])/;
const SEPARATORE_CODIFICATO = /%(2e|2f|5c)/i;

export const resolveRepresentativeBridgeNext = (next: string | null): string => {
  if (!next) return REPRESENTATIVE_BRIDGE_DEFAULT_NEXT;
  if (!PATH_INTERNO.test(next)) return REPRESENTATIVE_BRIDGE_DEFAULT_NEXT;
  if (DOT_SEGMENT.test(next)) return REPRESENTATIVE_BRIDGE_DEFAULT_NEXT;
  if (!AREA_ASSOCIATIVA.test(next)) return REPRESENTATIVE_BRIDGE_DEFAULT_NEXT;
  if (SEPARATORE_CODIFICATO.test(next)) return REPRESENTATIVE_BRIDGE_DEFAULT_NEXT;
  if (/[?&]bridge=/i.test(next)) return REPRESENTATIVE_BRIDGE_DEFAULT_NEXT;
  return next;
};
